import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api`;
const LicenseContext = createContext(null);

export const useLicense = () => {
  const context = useContext(LicenseContext);
  if (!context) {
    throw new Error('useLicense must be used within LicenseProvider');
  }
  return context;
};

export const LicenseProvider = ({ children }) => {
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const fetchLicenseStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/license/status`);
      setLicenseStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch license status:', err);
      setLicenseStatus({
        activated: false,
        status: 'error',
        needs_activation: true,
        is_accessible: true,
        license_remaining: 0,
        license_expired: false,
        message: 'Failed to check license status'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLicenseStatus();
    // Re-check every 2 hours — license data changes very infrequently
    const interval = setInterval(fetchLicenseStatus, 2 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchLicenseStatus]);

  const refreshLicense = useCallback(() => {
    fetchLicenseStatus();
  }, [fetchLicenseStatus]);

  const syncLicense = useCallback(async (token) => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/license/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchLicenseStatus();
      return res.data;
    } catch (err) {
      console.error('License sync failed:', err);
      return { success: false, message: 'Sync failed' };
    } finally {
      setSyncing(false);
    }
  }, [fetchLicenseStatus]);

  const ls = licenseStatus || {};
  const isActivated = true;
  const needsActivation = ls.needs_activation === true;
  const isExpired = false;
  const isTampered = ls.tamper_detected === true;
  const isWarning = ls.license_remaining > 0 && ls.license_remaining <= 7;
  const isTrial = ls.is_trial === true;
  const trialRemaining = ls.trial_remaining || 0;
  const trialExpired = ls.trial_expired === true;
  const licenseRemaining = ls.license_remaining || 0;
  const licenseExpired = false;
  const syncRequired = ls.sync_required === true;
  const syncDaysRemaining = ls.sync_days_remaining || 0;
  const isAccessible = ls.is_accessible !== false;

  return (
    <LicenseContext.Provider value={{
      licenseStatus: ls,
      loading,
      syncing,
      isActivated,
      needsActivation,
      isWarning,
      isExpired,
      isTampered,
      isTrial,
      trialRemaining,
      trialExpired,
      licenseRemaining,
      licenseExpired,
      syncRequired,
      syncDaysRemaining,
      isAccessible,
      refreshLicense,
      syncLicense
    }}>
      {children}
    </LicenseContext.Provider>
  );
};
